import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { DirectionService } from '../../direction.service';
import { AuthService } from 'src/app/auth/auth.service';
import { DialogService } from 'src/app/dialog/dialog.service';
import { GeneralManagementService } from '../../super-management/general-management.service';

@Component({
  selector: 'app-su-new-order-supplier',
  templateUrl: './su-new-order-supplier.component.html',
  styleUrls: ['./su-new-order-supplier.component.css'],
  host: {
    class: 'fill-screen-modal-new-dialog'
  }
})
export class SuNewOrderSupplierComponent implements OnInit, OnDestroy {
  isRTL: boolean = true;
  private directionSubscription: Subscription;
  isDarkMode: boolean = false;
  isLoading: boolean = false;

  userId: string;
  userIsAuthenticated = false;
  private authStatusSub: Subscription;

  // Branch data passed from parent component
  branch: any = null;
  printingService: string = '';
  isPicsPrinter: boolean = false;
  
  // New ink data from backend
  allInks: any[] = [];
  
  // Express papers data from backend
  allExpressPapers: any[] = [];
  
  // Edit mode management
  editMode: { [key: string]: { [key: string]: boolean } } = {};
  editValues: { [key: string]: { [key: string]: number } } = {};
  
  // Loading state for initial data
  isInitialDataLoaded: boolean = false;
  
  // Updated values storage for edit functionality
  updatedValues: { [key: string]: { [key: string]: number } } = {
    paper: {},
    ink: {}
  };
  
  // Order arrived at branch checkbox
  orderArrivedAtBranch: boolean = false;
  
  // Order summary display state
  showOrderSummary: boolean = false;
  
  // Supplier name
  supplierName: string = '';
  
  // Loading state for final order submission
  isSubmittingOrder: boolean = false;
  
  // Success state after order is created
  showSuccessMessage: boolean = false;
  
  // Default settings mode
  isDefaultSettingsMode: boolean = false;

  constructor(
    private directionService: DirectionService,
    private authService: AuthService,
    private dialogService: DialogService,
    private generalManagementService: GeneralManagementService,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) { }

  async ngOnInit() {
    this.isLoading = true;
    this.directionSubscription = this.directionService.direction$.subscribe(direction => {
      this.isRTL = direction === 'rtl';
    });

    this.directionService.isDarkMode$.subscribe(isDarkMode => {
      this.isDarkMode = isDarkMode;
    });

    this.userId = this.authService.getUserId();
    this.userIsAuthenticated = this.authService.getIsAuth();
    this.authStatusSub = this.authService
      .getAuthStatusListener()
      .subscribe(isAuthenticated => {
        this.userIsAuthenticated = isAuthenticated;
        this.userId = this.authService.getUserId();
      });

    // Receive branch data from parent component
    if (this.data) {
      this.branch = this.data.branch;
      this.printingService = this.data.printingService || '';
      this.isPicsPrinter = this.data.isPicsPrinter || false;
      console.log('Dialog data received:', this.data);
      
      // Initialize order quantities with stock needed values
      this.initializeOrderQuantities();
    }

    // Load inks from backend
    this.loadAllInks();
    
    // Load express papers from backend if it's express
    console.log('CHECK_EXPRESS_PAPERS - printingService:', this.printingService);
    if (this.printingService === 'express') {
      console.log('CHECK_EXPRESS_PAPERS - Calling loadAllExpressPapers()');
      this.loadAllExpressPapers();
    } else {
      console.log('CHECK_EXPRESS_PAPERS - NOT calling loadAllExpressPapers() because printingService is:', this.printingService);
    }

    this.isLoading = false;
  }

  closeSuNewOrderSupplierDialog() {
    this.dialogService.onCloseSuNewOrderSupplierDialog();
  }

  /**
   * Load all inks from backend
   */
  loadAllInks(): void {
    this.generalManagementService.getAllInks().subscribe({
      next: (inks) => {
        this.allInks = inks;
        this.isInitialDataLoaded = true;
        
        
        // Log complete dialog data including backend inks
        this.logCompleteDialogData();
      },
      error: (error) => {
        console.error('Error loading inks:', error);
        this.allInks = [];
        this.isInitialDataLoaded = true;
        
        // Log complete dialog data even if inks failed to load
        this.logCompleteDialogData();
      }
    });
  }

  /**
   * Load all express papers from backend
   */
  loadAllExpressPapers(): void {
    console.log('CHECK_EXPRESS_PAPERS [loadAllExpressPapers] - Starting load');
    this.generalManagementService.getAllExpressPapers().subscribe({
      next: (papers) => {
        console.log('CHECK_EXPRESS_PAPERS [loadAllExpressPapers] - Response received');
        this.allExpressPapers = papers;
        console.log('EXPRESS_PAPERS - Loaded express papers:', JSON.stringify(papers, null, 2));
        console.log('EXPRESS_PAPERS - Count:', papers.length);
      },
      error: (error) => {
        console.error('CHECK_EXPRESS_PAPERS [loadAllExpressPapers] - Error:', error);
        console.error('CHECK_EXPRESS_PAPERS [loadAllExpressPapers] - Error details:', JSON.stringify(error));
        console.error('Error loading express papers:', error);
        this.allExpressPapers = [];
      }
    });
  }

  /**
   * Log complete dialog data including all injected data and backend inks
   */
  private logCompleteDialogData(): void {
    const completeData = {
      dialogInjectedData: this.data,
      branch: this.branch,
      printingService: this.printingService,
      isPicsPrinter: this.isPicsPrinter,
      backendInks: this.allInks,
      paperTypes: this.getPaperTypes(),
      inkStatus: this.getInkStatus(),
      wasteInk: this.getWasteInk(),
      orderQuantities: this.orderQuantities
    };
    
    console.log('BRANCH_IN_DIALOG - Complete dialog data:', JSON.stringify(completeData, null, 2));
  }

  /**
   * Helper function to get branch serial_name from different possible locations
   */
  private getBranchSerialName(): string {
    if (!this.branch) return '';
    
    // Try to get serial_name from different possible locations
    const serialName = this.branch.serial_name || 
                       this.branch?.properties?.serial_name ||
                       (this.branch?.properties?.serial ? 
                         String(this.branch.properties.serial) : '') ||
                       '';
    
    // Debug log to help identify where serial_name is stored
    if (!serialName && this.branch) {
      console.log('DEBUG: Branch serial_name not found. Branch structure:', {
        hasSerialName: !!this.branch.serial_name,
        hasProperties: !!this.branch.properties,
        hasPropertiesSerialName: !!this.branch?.properties?.serial_name,
        hasPropertiesSerial: !!this.branch?.properties?.serial,
        branchKeys: Object.keys(this.branch)
      });
    }
    
    return serialName;
  }

  getBranchName(): string {
    if (!this.branch || !this.printingService) return '';
    
    const branchSerialName = this.getBranchSerialName();
    
    if (!branchSerialName) {
      return '';
    }
    
    // For express service (including pics printers which are express service),
    // use the express prefix. The pics printers use express service type but with isPicsPrinter flag.
    // The translation key format is: choose-branch.naming.express.{serial_name}
    // For example: choose-branch.naming.express.nocturno_pics
    if (this.printingService === 'express') {
      return `choose-branch.naming.express.${branchSerialName}`;
    }
    
    // For other services (plotter, etc.), use the service type directly
    return `choose-branch.naming.${this.printingService}.${branchSerialName}`;
  }

  getPrintingServiceIcon(): string {
    if (this.printingService === 'plotter') {
      return 'assets/images/plotter-icon.svg';
    }
    return `assets/images/${this.printingService}-icon.svg`;
  }

  toggleDefaultSettingsMode(): void {
    this.isDefaultSettingsMode = !this.isDefaultSettingsMode;
  }

  getDefaultSettingsModeText(): string {
    return this.isDefaultSettingsMode ? 'su-management.default-settings-mode-default' : 'su-management.default-settings-mode-normal';
  }

  getBranchIcon(): string {
    if (!this.branch) return '';
    
    const branchSerialName = this.getBranchSerialName();
    if (!branchSerialName) return '';
    return `assets/images/branches/${branchSerialName}.png`;
  }

  getDisplayText(): string {
    // For pics printer, still show branch name, but if branch name is not available, show general title
    const branchName = this.getBranchName();
    if (this.isPicsPrinter && branchName) {
      return branchName;
    } else if (this.isPicsPrinter && !branchName) {
      return 'choose-system.title-3dprint';
    }
    return branchName;
  }

  getPrintingServiceDisplayName(): string {
    if (this.isPicsPrinter) {
      return 'PICS';
    }
    
    switch (this.printingService) {
      case 'plotter':
        return 'PLOTTER';
      case 'express':
        return 'EXPRESS';
      case 'pics':
        return 'PICS';
      default:
        return this.printingService.toUpperCase();
    }
  }

  // Get printer data for plotter
  getPrinter(): any {
    if (this.branch && this.branch.printers && this.branch.printers.length > 0) {
      return this.branch.printers[0];
    }
    return null;
  }

  // Get paper types (inputBins)
  getPaperTypes(): any[] {
    if (this.printingService === 'express') {
      // For express, use consumables.papers
      if (this.branch?.consumables?.papers) {
        return this.branch.consumables.papers;
      }
    } else {
      // For plotter, use printers[0].inputBins
      const printer = this.getPrinter();
      if (printer && printer.inputBins) {
        return printer.inputBins;
      }
    }
    return [];
  }

  // Get ink status from printer
  getInkStatus(): any[] {
    if (this.printingService === 'express') {
      // For express, use consumables.ink but filter out Waste (show it in waste section)
      if (this.branch?.consumables?.ink) {
        return this.branch.consumables.ink.filter((ink: any) => ink.name !== 'Waste');
      }
    } else {
      // For plotter, use printers[0].inkStatus
      const printer = this.getPrinter();
      if (printer && printer.inkStatus) {
        return printer.inkStatus;
      }
    }
    return [];
  }

  // Get all inks from backend
  getAllInksFromBackend(): any[] {
    return this.allInks;
  }

  /**
   * Get express paper from backend based on serial_name
   * @param serialName - The serial_name from consumables.papers
   * @returns The matching express paper or null if not found
   */
  getExpressPaperFromBackend(serialName: string): any {
    if (!this.allExpressPapers || this.allExpressPapers.length === 0 || !serialName) {
      return null;
    }
    
    return this.allExpressPapers.find(paper => paper.paperName === serialName) || null;
  }

  /**
   * Get paper dimensions for express (paperHeight x paperWidth)
   * @param paper - The paper from getPaperTypes()
   * @returns Object with height and width in mm
   */
  getExpressPaperDimensions(paper: any): { height: number; width: number } | null {
    if (this.printingService !== 'express' || !paper) {
      return null;
    }
    
    const expressPaper = this.getExpressPaperFromBackend(paper.serial_name || paper.paperName);
    if (!expressPaper || !expressPaper.paperHeight || !expressPaper.paperWidth) {
      return null;
    }
    
    // Convert from micrometers to mm
    const height = Math.round(expressPaper.paperHeight / 1000);
    const width = Math.round(expressPaper.paperWidth / 1000);
    
    return { height, width };
  }

  /**
   * Get sheets per package (defaultAmount) for express
   * @param paper - The paper from getPaperTypes()
   * @returns Number of sheets per package or empty string
   */
  getExpressPaperSheetsPerPackage(paper: any): number {
    if (this.printingService !== 'express' || !paper) {
      return 0;
    }
    
    const expressPaper = this.getExpressPaperFromBackend(paper.serial_name || paper.paperName);
    return expressPaper?.defaultAmount || 0;
  }

  /**
   * Get default price for express paper
   * @param paper - The paper from getPaperTypes()
   * @returns Default price or 0
   */
  getExpressPaperDefaultPrice(paper: any): number {
    if (this.printingService !== 'express' || !paper) {
      return 0;
    }
    
    const expressPaper = this.getExpressPaperFromBackend(paper.serial_name || paper.paperName);
    return expressPaper?.defaultPrice || 0;
  }

  /**
   * Find ink type from backend inks based on color name (case insensitive)
   * @param inkColor - The color name from printer inkStatus
   * @returns The matching ink type from backend or null if not found
   */
  getInkTypeFromBackend(inkColor: string): any {
    if (!this.allInks || this.allInks.length === 0) {
      return null;
    }
    
    // Check if inkColor exists before calling toUpperCase
    if (!inkColor) {
      return null;
    }
    
    const upperInkColor = inkColor.toUpperCase();
    
    // Special handling for waste ink
    if (upperInkColor === 'WASTE') {
      const wasteInk = this.allInks.find(ink => ink.name && ink.name.toLowerCase() === 'waste');
      if (wasteInk) {
        return wasteInk;
      }
    }
    
    const found = this.allInks.find(ink => {
      // Try both 'name' and 'color' fields
      const inkName = ink.name ? ink.name.toUpperCase() : '';
      const inkColor = ink.color ? ink.color.toUpperCase() : '';
      return inkName === upperInkColor || inkColor === upperInkColor;
    });
    
    return found || null;
  }

  /**
   * Get default price for ink based on printing service
   * @param inkColor - The color name from printer inkStatus
   * @returns The default price for the ink
   */
  getInkDefaultPrice(inkColor: string): number {
    if (!inkColor) {
      return 0;
    }
    
    const inkKey = inkColor + '_price';
    if (this.updatedValues['ink'] && this.updatedValues['ink'][inkKey] !== undefined) {
      return this.updatedValues['ink'][inkKey];
    }
    
    const inkType = this.getInkTypeFromBackend(inkColor);
    
    if (!inkType || !inkType[this.printingService]) {
      return 0;
    }
    
    const serviceData = inkType[this.printingService];
    if (serviceData.defaultPrice) {
      return serviceData.defaultPrice;
    }
    
    return 0;
  }

  /**
   * Get default amount for ink based on printing service
   * @param inkColor - The color name from printer inkStatus
   * @returns The default amount for the ink
   */
  getInkDefaultAmount(inkColor: string): number {
    if (!inkColor) {
      return 0;
    }
    
    const inkKey = inkColor + '_amount';
    if (this.updatedValues['ink'] && this.updatedValues['ink'][inkKey] !== undefined) {
      return this.updatedValues['ink'][inkKey];
    }
    
    const inkType = this.getInkTypeFromBackend(inkColor);
    
    if (!inkType || !inkType[this.printingService]) {
      return 0;
    }
    
        const serviceData = inkType[this.printingService];
        if (serviceData.defaultAmount) {
          return serviceData.defaultAmount;
        }
    
    return 0;
  }

  // Get ink identifier (color or name based on printing service)
  getInkIdentifier(ink: any): string {
    if (this.printingService === 'express') {
      return ink?.name || '';
    } else {
      return ink?.color || '';
    }
  }

  /**
   * Get ribon type from backend inks
   * @returns The ribon ink type from backend or null if not found
   */
  getRibonTypeFromBackend(): any {
    if (!this.allInks || this.allInks.length === 0) {
      return null;
    }
    
    const ribonInk = this.allInks.find(ink => 
      ink.name && ink.name.toLowerCase() === 'ribon'
    );
    
    return ribonInk || null;
  }

  /**
   * Get default price for ribon
   * @returns The default price for ribon
   */
  getRibonDefaultPrice(): number {
    const ribonKey = 'ribon_price';
    if (this.updatedValues['ink'] && this.updatedValues['ink'][ribonKey] !== undefined) {
      return this.updatedValues['ink'][ribonKey];
    }
    
    const ribonType = this.getRibonTypeFromBackend();
    
    if (!ribonType || !ribonType[this.printingService]) {
      return 0;
    }
    
    const serviceData = ribonType[this.printingService];
    if (serviceData.defaultPrice) {
      return serviceData.defaultPrice;
    }
    
    return 0;
  }

  /**
   * Get default amount for ribon
   * @returns The default amount for ribon
   */
  getRibonDefaultAmount(): number {
    const ribonKey = 'ribon_amount';
    if (this.updatedValues['ink'] && this.updatedValues['ink'][ribonKey] !== undefined) {
      return this.updatedValues['ink'][ribonKey];
    }
    
    const ribonType = this.getRibonTypeFromBackend();
    
    if (!ribonType || !ribonType[this.printingService]) {
      return 0;
    }
    
    const serviceData = ribonType[this.printingService];
    if (serviceData.defaultAmount) {
      return serviceData.defaultAmount;
    }
    
    return 0;
  }

  /**
   * Get ribon stock (ribons or images)
   * @param type - 'ribons' or 'images'
   * @returns The stock value
   */
  getRibonStock(type: 'ribons' | 'images'): number {
    if (this.branch && this.branch.stockCurrent && this.branch.stockCurrent.paper) {
      if (type === 'ribons' && this.branch.stockCurrent.paper.ribons !== undefined) {
        return this.branch.stockCurrent.paper.ribons;
      } else if (type === 'images' && this.branch.stockCurrent.paper.images !== undefined) {
        return this.branch.stockCurrent.paper.images;
      }
    }
    return 0;
  }

  // Edit mode functions
  isInEditMode(type: string, item: string): boolean {
    return this.editMode[type] && this.editMode[type][item] === true;
  }

  startEdit(type: string, item: string, currentValue: number): void {
    if (!this.editMode[type]) {
      this.editMode[type] = {};
    }
    if (!this.editValues[type]) {
      this.editValues[type] = {};
    }
    
    // Close all other edit modes before opening new one
    Object.keys(this.editMode).forEach(category => {
      if (this.editMode[category]) {
        Object.keys(this.editMode[category]).forEach(key => {
          if (category === type && key === item) {
            // Keep the current edit mode
            return;
          }
          if (this.editMode[category][key]) {
            // Close edit mode without saving changes (like clicking X)
            this.editMode[category][key] = false;
            this.editValues[category][key] = undefined;
          }
        });
      }
    });
    
    this.editMode[type][item] = true;
    this.editValues[type][item] = currentValue;
  }

  cancelEdit(type: string, item: string): void {
    if (this.editMode[type]) {
      this.editMode[type][item] = false;
    }
  }

  confirmEdit(type: string, item: string): void {
    // TODO: Implement save logic here
    if (this.editMode[type]) {
      this.editMode[type][item] = false;
    }
  }

  // Apply changes functions
  hasMultipleItemsInCategory(type: string): boolean {
    if (type === 'paper') {
      // Check paper types
      const paperTypes = this.getPaperTypes();
      return paperTypes && paperTypes.length > 1;
    } else if (type === 'ink') {
      // For express, use consumables.ink length
      if (this.printingService === 'express') {
        const inkStatus = this.getInkStatus();
        return inkStatus && inkStatus.length > 1;
      } else {
        // For plotter, check stockCurrent.ink
        const inkColors = Object.keys(this.branch?.stockCurrent?.ink || {})
          .filter(color => color !== 'waste');
        return inkColors.length > 1;
      }
    } else if (type === 'waste') {
      // For waste, there's only one item
      return false;
    }
    return false;
  }

  applyToCurrentType(type: string, item: string): void {
    const newValue = this.getEditValue(type, item);
    
    // Update only the current item
    if (!this.updatedValues[type]) {
      this.updatedValues[type] = {};
    }
    this.updatedValues[type][item] = newValue;
    
    console.log('Applied to current type:', type, item, 'new value:', newValue);
    
    // If in default settings mode, send to backend
    if (this.isDefaultSettingsMode) {
      this.sendUpdateToBackend(type, item, newValue, false);
    }
    
    this.confirmEdit(type, item);
  }

  applyToAllTypes(type: string, item: string): void {
    const newValue = this.getEditValue(type, item);
    
    // Update all items of the same type in the same category
    if (!this.updatedValues[type]) {
      this.updatedValues[type] = {};
    }
    
    if (type === 'paper') {
      // Apply to all paper types from branch.stockCurrent.paper
      if (this.branch?.stockCurrent?.paper) {
        Object.keys(this.branch.stockCurrent.paper).forEach(paperType => {
          const paperKey = paperType + '_' + item.split('_')[1];
          this.updatedValues[type][paperKey] = newValue;
        });
      }
    } else if (type === 'ink') {
      // Apply to all ink colors from branch.stockCurrent.ink (excluding waste)
      if (this.branch?.stockCurrent?.ink) {
        Object.keys(this.branch.stockCurrent.ink)
          .filter(color => color !== 'waste' && color !== 'Waste')
          .forEach(inkColor => {
            const inkKey = inkColor + '_' + item.split('_')[1];
            this.updatedValues[type][inkKey] = newValue;
          });
      }
    }
    
    console.log('Applied to all types:', type, item, 'new value:', newValue);
    console.log('Updated values:', this.updatedValues);
    
    // If in default settings mode, send to backend
    if (this.isDefaultSettingsMode) {
      this.sendUpdateToBackend(type, item, newValue, true);
    }
    
    this.confirmEdit(type, item);
  }

  sendUpdateToBackend(type: string, item: string, newValue: number, applyToAll: boolean): void {
    // Prepare the update data
    const updateData = {
      branchId: this.branch?.id || this.branch?._id,
      printingService: this.printingService,
      isPicsPrinter: this.isPicsPrinter,
      category: type,
      itemKey: item,
      newValue: newValue,
      applyToAll: applyToAll,
      updatedValues: this.updatedValues
    };

    console.log('=== SENDING UPDATE TO BACKEND ===');
    console.log('Update data:', updateData);
    console.log('====================================');

    this.generalManagementService.updateDefaultSettings(updateData).subscribe({
      next: (response) => {
        console.log('Default settings updated successfully in backend:', response);
        console.log('Backend response data:', JSON.stringify(response));
        
        // Find and update the specific ink in allInks array
        const inkColor = item.split('_')[0];
        const propertyName = item.split('_')[1]; // 'price' or 'amount'
        
        // For waste in express, it might not be in allInks array
        if (inkColor === 'waste' || inkColor === 'Waste') {
          console.log('Updated waste settings - no need to update allInks');
          // Waste settings don't need to update allInks array
          return;
        }
        
        const ink = this.allInks.find(ink => ink.name === inkColor || ink.color === inkColor);
        if (ink && ink[this.printingService]) {
          if (propertyName === 'price') {
            ink[this.printingService].defaultPrice = newValue;
          } else if (propertyName === 'amount') {
            ink[this.printingService].defaultAmount = newValue;
          }
          console.log('Updated ink in local allInks:', ink);
        }
        
        // Don't update this.branch from response.updatedData - it might overwrite important data
        // The backend update only affects the ink defaults, not the branch structure
      },
      error: (error) => {
        console.error('Error updating default settings in backend:', error);
        // Continue with local update even if backend fails
      }
    });
  }
  

  getEditValue(type: string, item: string): number {
    return this.editValues[type] && this.editValues[type][item] ? this.editValues[type][item] : 0;
  }

  setEditValue(type: string, item: string, value: number): void {
    if (!this.editValues[type]) {
      this.editValues[type] = {};
    }
    this.editValues[type][item] = value;
  }

  // Check if value has been modified
  hasValueChanged(type: string, item: string, originalValue: number): boolean {
    const editValue = this.getEditValue(type, item);
    return editValue !== originalValue;
  }

  // Get waste ink info
  getWasteInk(): any {
    if (this.printingService === 'express') {
      // For express, find Waste in consumables.ink
      if (this.branch?.consumables?.ink) {
        return this.branch.consumables.ink.find((ink: any) => ink.name === 'Waste');
      }
    } else {
      // For plotter, use printer.wasteInk
      const printer = this.getPrinter();
      if (printer && printer.wasteInk) {
        return printer.wasteInk;
      }
    }
    return null;
  }

  // Get current stock for paper
  getPaperStock(paperType: string): number {
    // For both express and plotter, use stockCurrent.paper
    if (this.branch && this.branch.stockCurrent && this.branch.stockCurrent.paper) {
      return this.branch.stockCurrent.paper[paperType] || 0;
    }
    return 0;
  }

  // Get current stock for ink
  getInkStock(inkColor: string): number {
    if (this.branch && this.branch.stockCurrent && this.branch.stockCurrent.ink) {
      return this.branch.stockCurrent.ink[inkColor] || 0;
    }
    return 0;
  }

  // Get needed stock for paper
  getPaperNeeded(paperType: string): number {
    if (this.branch && this.branch.stockNeeded && this.branch.stockNeeded.paper) {
      return this.branch.stockNeeded.paper[paperType] || 0;
    }
    return 0;
  }

  // Get needed stock for ink
  getInkNeeded(inkColor: string): number {
    if (this.printingService === 'express') {
      // For express, stock needed is not relevant (managed by printer)
      return 0;
    }
    if (this.branch && this.branch.stockNeeded && this.branch.stockNeeded.ink) {
      return this.branch.stockNeeded.ink[inkColor] || 0;
    }
    return 0;
  }

  // Get waste ink stock
  getWasteStock(): number {
    if (this.printingService === 'express') {
      // For express, use 'Waste' with capital W
      return this.getInkStock('Waste');
    }
    return this.getInkStock('waste');
  }

  // Get waste ink needed
  getWasteNeeded(): number {
    if (this.printingService === 'express') {
      // For express, stock needed is not relevant
      return 0;
    }
    return this.getInkNeeded('waste');
  }

  // Order quantity management
  private orderQuantities: { [key: string]: { [key: string]: number } } = {
    paper: {},
    ink: {},
    waste: {}
  };

  getOrderQuantity(type: string, item: string): number {
    return this.orderQuantities[type]?.[item] || 0;
  }

  setOrderQuantity(type: string, item: string, value: number): void {
    if (!this.orderQuantities[type]) {
      this.orderQuantities[type] = {};
    }
    this.orderQuantities[type][item] = Math.max(0, value);
  }

  changeOrderQuantity(action: 'add' | 'remove', type: string, item: string): void {
    const currentValue = this.getOrderQuantity(type, item);
    if (action === 'add') {
      this.setOrderQuantity(type, item, currentValue + 1);
    } else if (action === 'remove') {
      this.setOrderQuantity(type, item, Math.max(0, currentValue - 1));
    }
  }

  ngOnDestroy() {
    if (this.directionSubscription) {
      this.directionSubscription.unsubscribe();
    }
    if (this.authStatusSub) {
      this.authStatusSub.unsubscribe();
    }
  }

  /**
   * Get paper default price (from updated values or original data)
   */
  getPaperDefaultPrice(paperType: string): number {
    const paperKey = paperType + '_price';
    if (this.updatedValues['paper'] && this.updatedValues['paper'][paperKey] !== undefined) {
      return this.updatedValues['paper'][paperKey];
    }
    
    if (this.printingService === 'express') {
      // For express, get price from express_papers imported from backend
      const expressPaper = this.getExpressPaperFromBackend(paperType);
      return expressPaper?.defaultPrice || 0;
    } else {
      // For plotter, use paperType.paperType
      const paperData = this.getPaperTypes().find(p => p.paperType.paperType === paperType);
      if (paperData && paperData.paperType.defaultPrice) {
        return paperData.paperType.defaultPrice;
      }
    }
    
    return 0;
  }
  
  /**
   * Get paper default length (from updated values or original data)
   */
  getPaperDefaultLength(paperType: string): number {
    const paperKey = paperType + '_length';
    if (this.updatedValues['paper'] && this.updatedValues['paper'][paperKey] !== undefined) {
      return this.updatedValues['paper'][paperKey];
    }
    
    if (this.printingService === 'express') {
      // For express, get defaultAmount from express_papers
      const expressPaper = this.getExpressPaperFromBackend(paperType);
      return expressPaper?.defaultAmount || 0;
    } else {
      // For plotter, use paperType.defaultLength
      const paperData = this.getPaperTypes().find(p => p.paperType.paperType === paperType);
      if (paperData && paperData.paperType.defaultLength) {
        return paperData.paperType.defaultLength;
      }
    }
    
    return 0;
  }

  /**
   * Get order summary items (only items with quantity > 0)
   */
  getOrderSummaryItems(): any[] {
    const items: any[] = [];
    
    // For pics printer in express mode, handle ribon items
    if (this.isPicsPrinter && this.printingService === 'express') {
      // Ribon ribons (images is just for display, not ordering)
      const ribonsQuantity = this.getOrderQuantity('paper', 'ribons');
      if (ribonsQuantity > 0) {
        const price = this.getRibonDefaultPrice();
        const amount = this.getRibonDefaultAmount();
        items.push({
          type: 'ink',
          name: this.translateService.instant('su-management.ribon'),
          quantity: ribonsQuantity,
          amount: `${amount} ${this.translateService.instant('su-management.units.short')}`,
          unitPrice: price,
          totalPrice: ribonsQuantity * price
        });
      }
      
      return items;
    }
    
    // For express, use stockCurrent structure
    if (this.printingService === 'express') {
      // Paper items
      if (this.branch?.stockCurrent?.paper) {
        Object.keys(this.branch.stockCurrent.paper).forEach(paperType => {
          const quantity = this.getOrderQuantity('paper', paperType);
          if (quantity > 0) {
            // Find paper from consumables to get the paperName
            const paperFromConsumables = this.branch?.consumables?.papers?.find((p: any) => p.serial_name === paperType || p.paperName === paperType);
            const paperName = paperFromConsumables?.paperName || paperType;
            const price = this.getPaperDefaultPrice(paperType);
            const length = this.getPaperDefaultLength(paperType);
            items.push({
              type: 'paper',
              name: paperName,
              quantity: quantity,
              amount: `${length} דפים`,
              unitPrice: price,
              totalPrice: quantity * price
            });
          }
        });
      }
      
      // Ink items
      if (this.branch?.stockCurrent?.ink) {
        Object.keys(this.branch.stockCurrent.ink).forEach(inkColor => {
          if (inkColor !== 'Waste' && inkColor !== 'waste') {
            const quantity = this.getOrderQuantity('ink', inkColor);
            if (quantity > 0) {
              const price = this.getInkDefaultPrice(inkColor);
              const amount = this.getInkDefaultAmount(inkColor);
              items.push({
                type: 'ink',
                name: inkColor + ' (' + this.translateService.instant('ink-type.name.' + inkColor) + ')',
                quantity: quantity,
                amount: `${amount} מ"ל`,
                unitPrice: price,
                totalPrice: quantity * price
              });
            }
          }
        });
      }
      
      // Waste ink
      const wasteQuantity = this.getOrderQuantity('waste', 'waste');
      if (wasteQuantity > 0) {
        const price = this.getInkDefaultPrice('Waste');
        items.push({
          type: 'waste',
          name: this.translateService.instant('ink-type.name.Waste'),
          quantity: wasteQuantity,
          amount: '',
          unitPrice: price,
          totalPrice: wasteQuantity * price
        });
      }
      
      return items;
    }
    
    // For plotter, use stockNeeded structure
    // Paper items
    if (this.branch?.stockNeeded?.paper) {
      Object.keys(this.branch.stockNeeded.paper).forEach(paperType => {
        const quantity = this.getOrderQuantity('paper', paperType);
        if (quantity > 0) {
          const paperData = this.getPaperTypes().find(p => p.paperType.paperType === paperType);
          if (paperData) {
            const price = this.getPaperDefaultPrice(paperType);
            const length = this.getPaperDefaultLength(paperType);
            items.push({
              type: 'paper',
              name: paperData.paperType.paperName,
              quantity: quantity,
              amount: `${length} מ'`,
              unitPrice: price,
              totalPrice: quantity * price
            });
          }
        }
      });
    }
    
    // Ink items
    if (this.branch?.stockNeeded?.ink) {
      Object.keys(this.branch.stockNeeded.ink).forEach(inkColor => {
        if (inkColor !== 'waste') {
          const quantity = this.getOrderQuantity('ink', inkColor);
          if (quantity > 0) {
            const price = this.getInkDefaultPrice(inkColor);
            const amount = this.getInkDefaultAmount(inkColor);
            items.push({
              type: 'ink',
              name: inkColor + ' (' + this.translateService.instant('ink-type.name.' + inkColor) + ')',
              quantity: quantity,
              amount: `${amount} מ"ל`,
              unitPrice: price,
              totalPrice: quantity * price
            });
          }
        }
      });
    }
    
    // Waste ink
    const wasteQuantity = this.getOrderQuantity('waste', 'waste');
    if (wasteQuantity > 0) {
      const price = this.getInkDefaultPrice('waste');
      items.push({
        type: 'waste',
        name: this.translateService.instant('ink-type.name.Waste'),
        quantity: wasteQuantity,
        amount: '', // Empty for waste ink
        unitPrice: price,
        totalPrice: wasteQuantity * price
      });
    }
    
    return items;
  }
  
  /**
   * Get total order price
   */
  getTotalOrderPrice(): number {
    return this.getOrderSummaryItems().reduce((total, item) => total + item.totalPrice, 0);
  }
  
  /**
   * Confirm order and show summary
   */
  confirmOrder(): void {
    console.log('Confirm order clicked');
    console.log('Order quantities:', this.orderQuantities);
    console.log('Updated values:', this.updatedValues);
    console.log('Order summary items:', this.getOrderSummaryItems());
    console.log('Total price:', this.getTotalOrderPrice());
    this.showOrderSummary = true;
  }
  
  /**
   * Go back to order form
   */
  backToOrderForm(): void {
    this.showOrderSummary = false;
  }
  
  /**
   * Check if order button should be disabled (all quantities are 0 OR supplier is invalid)
   */
  isOrderButtonDisabled(): boolean {
    // Check if supplier is valid first
    if (!this.isSupplierValid()) {
      return true; // Disable if supplier is not valid
    }
    
    // Check for pics printer in express mode
    if (this.isPicsPrinter && this.printingService === 'express') {
      // Check ribon ribons quantity (images is just for display, not ordering)
      if (this.getOrderQuantity('paper', 'ribons') > 0) {
        return false;
      }
      return true; // All quantities are 0
    }
    
    // Check for express structure
    if (this.printingService === 'express') {
      // Check paper quantities
      if (this.branch?.consumables?.papers) {
        for (const paper of this.branch.consumables.papers) {
          const quantity = this.getOrderQuantity('paper', paper.serial_name || paper.paperName);
          if (quantity > 0) {
            return false;
          }
        }
      }
      
      // Check ink quantities (excluding Waste)
      if (this.branch?.consumables?.ink) {
        for (const ink of this.branch.consumables.ink) {
          if (ink.name === 'Waste') continue; // Skip waste from ink
          const quantity = this.getOrderQuantity('ink', ink.name);
          if (quantity > 0) {
            return false;
          }
        }
      }
      
      // Check waste quantities
      if (this.getOrderQuantity('waste', 'waste') > 0) {
        return false; // Found waste with quantity > 0
      }
      
      return true; // All quantities are 0
    }
    
    // Check for plotter structure
    // Check paper quantities
    if (this.branch?.stockNeeded?.paper) {
      for (const paperType of Object.keys(this.branch.stockNeeded.paper)) {
        if (this.getOrderQuantity('paper', paperType) > 0) {
          return false; // Found at least one paper with quantity > 0
        }
      }
    }
    
    // Check ink quantities
    if (this.branch?.stockNeeded?.ink) {
      for (const inkColor of Object.keys(this.branch.stockNeeded.ink)) {
        if (this.getOrderQuantity('ink', inkColor) > 0) {
          return false; // Found at least one ink with quantity > 0
        }
      }
    }
    
    // Check waste ink quantity
    if (this.getOrderQuantity('waste', 'waste') > 0) {
      return false; // Found waste ink with quantity > 0
    }
    
    return true; // All quantities are 0, disable button
  }
  
  /**
   * Initialize order quantities to 0 for new order
   */
  private initializeOrderQuantities(): void {
    // For pics printer in express mode, initialize ribon quantities
    if (this.isPicsPrinter && this.printingService === 'express') {
      this.orderQuantities['paper']['images'] = 0;
      this.orderQuantities['paper']['ribons'] = 0;
    } else {
      // For both express and plotter, use stockCurrent structure
      if (this.branch?.stockCurrent) {
        if (this.branch.stockCurrent.paper) {
          Object.keys(this.branch.stockCurrent.paper).forEach(paperType => {
            this.orderQuantities['paper'][paperType] = 0;
          });
        }
        
        if (this.branch.stockCurrent.ink) {
          Object.keys(this.branch.stockCurrent.ink).forEach(inkColor => {
            this.orderQuantities['ink'][inkColor] = 0;
          });
        }
      }
      
      // Also initialize waste for plotter
      if (this.printingService === 'plotter') {
        this.orderQuantities['waste']['waste'] = 0;
      }
    }
  }

  /**
   * Check if supplier name is valid (at least 2 characters)
   */
  isSupplierValid(): boolean {
    return this.supplierName && this.supplierName.trim().length >= 2;
  }

  /**
   * Confirm final order and submit to backend
   */
  confirmFinalOrder(): void {
    if (this.isSubmittingOrder) {
      return; // Prevent multiple submissions
    }

    this.isSubmittingOrder = true;

    // Prepare order data
    // Clean orderQuantities - remove images for pics printer (it's only for display, not ordering)
    let cleanedOrderQuantities = JSON.parse(JSON.stringify(this.orderQuantities)); // Deep copy
    if (this.isPicsPrinter && this.printingService === 'express') {
      // Remove images from paper orders (it's only for display)
      if (cleanedOrderQuantities.paper && cleanedOrderQuantities.paper.images !== undefined) {
        delete cleanedOrderQuantities.paper.images;
      }
    }
    
    const orderData = {
      supplierName: this.supplierName,
      orderArrivedAtBranch: this.orderArrivedAtBranch,
      branchId: this.branch?.id || this.branch?._id,
      branchSerialName: this.getBranchSerialName(),
      printingService: this.printingService,
      printingServiceName: this.getPrintingServiceDisplayName(),
      isPicsPrinter: this.isPicsPrinter,
      orderQuantities: cleanedOrderQuantities,
      updatedValues: this.updatedValues,
      orderSummaryItems: this.getOrderSummaryItems(),
      totalOrderPrice: this.getTotalOrderPrice(),
      userId: this.userId,
      userEmail: this.authService.getEmail() || null,
      createdAt: new Date().toISOString()
    };

    // Validate critical fields before submission
    if (!this.userId || this.userId === 'undefined' || (typeof this.userId === 'string' && this.userId.trim() === '')) {
      this.isSubmittingOrder = false;
      return;
    }
    
    if (!orderData.branchId || orderData.branchId === 'undefined') {
      this.isSubmittingOrder = false;
      return;
    }

    // Submit order to backend
    this.generalManagementService.createSupplierOrder(orderData).subscribe({
      next: (response) => {
        this.isSubmittingOrder = false;
        this.showSuccessMessage = true;
        
        // Show success message for 2 seconds, then close dialog and emit event
        setTimeout(() => {
          this.dialogService.supplierOrderCreated$.next();
          this.dialogService.onCloseSuNewOrderSupplierDialog();
        }, 2000);
      },
      error: (error) => {
        this.isSubmittingOrder = false;
        this.showSuccessMessage = false;
      }
    });
  }

  // ===============
}
